<template>
  <section class="mb-10 bg-white rounded-2xl shadow-sm p-5">
    <div class="flex items-center gap-2 mb-5">
      <h2 class="text-xl font-bold text-gray-800">
        {{ entity?.name ?? $t("statistics.no_data") }}
      </h2>
      <p
        v-if="entity?.auto_report_enabled"
        class="text-xs font-medium px-2.5 py-1 rounded-full bg-[#E8F4F3] text-[#06BBC4]"
      >
        {{ $t("statistics.auto_report") }}
      </p>
      <p
        class="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 capitalize"
      >
        {{ entityType }}
      </p>
    </div>

    <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
      <StatCard
        :label="$t('statistics.total_users')"
        :value="stats?.total_users ?? 0"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#06BBC4"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
        </template>
      </StatCard>

      <StatCard
        :label="$t('statistics.cyberscore_avg')"
        :value="cyberscoreDisplay"
        :as-progress="true"
        :progress="cyberscoreNum"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#06BBC4"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </template>
      </StatCard>

      <StatCard
        :label="$t('statistics.progression_avg')"
        :value="progressionDisplay"
        :as-progress="true"
        :progress="progressionNum"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#06BBC4"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        </template>
      </StatCard>

      <StatCard
        :label="$t('statistics.certified_users')"
        :value="certifiedDisplay"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#06BBC4"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="8" r="6" />
            <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
          </svg>
        </template>
      </StatCard>

      <StatCard
        :label="$t('statistics.cert_score_avg')"
        :value="certScoreDisplay"
        :as-progress="true"
        :progress="certScoreAvg"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#06BBC4"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon
              points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
            />
          </svg>
        </template>
      </StatCard>
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
      <!-- Microlearning -->
      <StatCard
        :label="$t('statistics.micro_learning')"
        :value="microList.length"
        :subtext="microSubtext"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#06BBC4"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.5 12 19.5 19.5 0 0 1 1.15 3.18 2 2 0 0 1 3.12 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 8 8l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 23 17z"
            />
          </svg>
        </template>
      </StatCard>

      <!-- Phishing -->
      <StatCard
        :label="$t('statistics.phishing_campaigns')"
        :value="phishingList.length"
        :subtext="phishingSubtext"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#06BBC4"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </template>
      </StatCard>

      <!-- Smishing -->
      <StatCard
        :label="$t('statistics.smshing_campaigns')"
        :value="smshingList.length"
        :subtext="smshingSubtext"
      >
        <template #icon>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#06BBC4"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            />
          </svg>
        </template>
      </StatCard>
    </div>

    <template v-if="microList.length">
      <h4
        class="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3"
      >
        {{ $t("statistics.micro_learning") }}
      </h4>
      <CampaignsTable :campaigns="microList" type="micro" />
    </template>

    <template v-if="phishingList.length">
      <h4
        class="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3"
      >
        {{ $t("statistics.phishing_campaigns") }}
      </h4>
      <CampaignsTable :campaigns="phishingList" type="phishing" />
    </template>

    <template v-if="smshingList.length">
      <h4
        class="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3"
      >
        {{ $t("statistics.smshing_campaigns") }}
      </h4>
      <CampaignsTable :campaigns="smshingList" type="smishing" />
    </template>
  </section>
</template>

<script setup>
import { defineProps, computed } from "vue";
import StatCard from "./StatCard.vue";
import CampaignsTable from "./CampaignsTable.vue";

const props = defineProps({
  entity: { type: Object, default: null },
  entityType: { type: String, default: "" },
});

const stats = computed(() => props.entity?.stats ?? {});

const cyberscoreNum = computed(() => Number(stats.value?.cyberscore_avg ?? 0));
const cyberscoreDisplay = computed(() => `${cyberscoreNum.value}%`);

const progressionNum = computed(() =>
  Number(stats.value?.progression_avg ?? 0),
);
const progressionDisplay = computed(() => `${progressionNum.value}%`);

const totalUsers = computed(() => stats.value?.total_users ?? 0);
const certifiedUsers = computed(() => stats.value?.certified_users ?? 0);
const certifiedDisplay = computed(() =>
  totalUsers.value ? `${certifiedUsers.value}` : String(certifiedUsers.value),
);
const certScoreAvg = computed(() => Number(stats.value?.cert_score_avg ?? 0));
const certScoreDisplay = computed(() => `${certScoreAvg.value}`);

const microList = computed(() =>
  Array.isArray(stats.value?.micro_learning_campaigns)
    ? stats.value.micro_learning_campaigns
    : [],
);
const phishingList = computed(() =>
  Array.isArray(stats.value?.phishing_campaigns)
    ? stats.value.phishing_campaigns
    : [],
);
const smshingList = computed(() =>
  Array.isArray(stats.value?.smshing_campaigns)
    ? stats.value.smshing_campaigns
    : [],
);

function sumField(list, field) {
  return list.reduce((acc, c) => acc + (Number(c[field]) || 0), 0);
}
// function avgField(list, field) {
//   if (!list.length) return 0;
//   const total = list.reduce((acc, c) => acc + (Number(c[field]) || 0), 0);
//   return Math.round((total / list.length) * 10) / 10;
// }
function fmt(n) {
  return n ?? 0;
}
// function pct(n) {
//   return `<strong>${fmt(n)}%</strong>`;
// }
function bold(n) {
  return `<strong>${fmt(n)}</strong>`;
}

const microSubtext = computed(() => {
  const list = microList.value;
  const sent = sumField(list, "total_sent");
  const opened = sumField(list, "opened");
  return `Envoyés\u00a0: ${bold(sent)} &bull; Ouverts\u00a0: ${bold(
    opened,
  )}`;
});

const phishingSubtext = computed(() => {
  const list = phishingList.value;
  const sent = sumField(list, "sent");
  const clicked = sumField(list, "clicked");
  const reported = sumField(list, "reported");
  return `Envoyés\u00a0: ${bold(
    sent,
  )} &bull; Cliqués\u00a0: <strong class="text-red-500">${fmt(
    clicked,
  )}</strong> &bull; Signalés\u00a0: <strong class="text-green-600">${fmt(
    reported,
  )}`;
});

const smshingSubtext = computed(() => {
  const list = smshingList.value;
  const sent = sumField(list, "sent");
  const clicked = sumField(list, "clicked");
  return `Envoyés\u00a0: ${bold(
    sent,
  )} &bull; Cliqués\u00a0: <strong class="text-red-500">${fmt(
    clicked,
  )}`;
});
</script>
